import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { DateTime } from 'luxon';
import {
    Box, Button, Checkbox, DialogActions, DialogContent, FormControlLabel, Grid, IconButton,
    InputAdornment, MenuItem, TextField
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { VoyageModel } from 'models/VoyageModel';
import { ModalActionProps } from 'models/AutoModals';
import useVesselModals from 'modules/vessels/hooks/useVesselModals';
import usePortModals from 'modules/yards/hooks/usePortModals';
import usePorts from 'modules/yards/hooks/usePorts';
import vesselList from 'recoil/atoms/vesselList';
import loadingYardList from 'recoil/selectors/loadingYardList';


type VoyageDialogProps = {
    voyage?: VoyageModel,
    create?: boolean,
} & ModalActionProps<VoyageModel>

const VoyageDialog = ({ create, onFinish, voyage }: VoyageDialogProps) => {
    const {t} = useTranslation();
    const vessels = useRecoilValue(vesselList);
    const yards = useRecoilValue(loadingYardList);
    const { loadingPorts, destinationPorts } = usePorts();
    const { openAddVesselModal } = useVesselModals();
    const { openAddPortModal } = usePortModals();

    const [ dates, setDates ] = useState<{[k: string]: DateTime|null|undefined}>({
        cutDate: voyage ? DateTime.fromISO(voyage.cutDate) : null,
        carDeliveryStartDate: voyage ? DateTime.fromISO(voyage.carDeliveryStartDate) : null,
        carDeliveryEndDate: voyage ? DateTime.fromISO(voyage.carDeliveryEndDate) : null,
        loadingStartDate: voyage ? DateTime.fromISO(voyage.loadingStartDate) : null,
        loadingEndDate: voyage ? DateTime.fromISO(voyage.loadingEndDate) : null,
        etd: voyage ? DateTime.fromISO(voyage.etd) : null,
        eta: voyage ? DateTime.fromISO(voyage.eta) : null,
        blIssueDate: voyage ? DateTime.fromISO(voyage.blIssueDate) : null,
    });

    const [state, setState] = useState(voyage || {} as VoyageModel);

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const data = Object.fromEntries(new FormData(event.currentTarget).entries()) as unknown as VoyageModel;

        onFinish({
            ...state,
            ...data,
            ...Object.keys(dates).reduce((ret, k) => ({...ret, [k]: dates[k]?.toISODate()}), {}),
            allowDismantled: Boolean(data.allowDismantled),
        });
    };

    const setCutDate = (d: DateTime | null | undefined) => {
        setDates(old => ({
            ...old,
            cutDate: d,
            carDeliveryEndDate: old.carDeliveryEndDate || d,
        }))
    };

    const setLoadingStart = (d: DateTime | null | undefined) => {
        setDates(old => ({
            ...old,
            cutDate: old.cutDate || d?.minus({ days: 7 }),
            carDeliveryStartDate: old.carDeliveryStartDate || DateTime.min(DateTime.now(), d?.minus({ days: 8 }) || DateTime.now()),
            carDeliveryEndDate: old.carDeliveryEndDate || d?.minus({ days: 7 }),
            loadingStartDate: d,
            loadingEndDate: old.loadingEndDate || d,
            etd: old.etd || d,
        }))
    };

    const setEta = (d: DateTime | null | undefined) => {
        setDates(old => ({
            ...old,
            eta: d,
            blIssueDate: old.blIssueDate || (d?.plus({ days: 1 })),
        }))
    };

    const curPol = loadingPorts.find(p => p.id === state.polId);
    const isTransfer =  curPol && curPol.country !== 'JP';

    return (
        <Box component="form" onSubmit={submit}>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                        <TextField
                            select
                            id="vessel"
                            name="vesselId"
                            label={t('vessel')}
                            fullWidth
                            required
                            value={state.vesselId || ''}
                            onChange={e => setState(old => ({...old, vesselId: Number(e.target.value)}))}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">
                                    <IconButton
                                        aria-label="add vessel"
                                        onClick={() => openAddVesselModal().then(vesselId => setState(old => ({...old, vesselId})))}
                                    >
                                        <Add />
                                    </IconButton>
                                </InputAdornment>,
                            }}
                        >
                            {vessels.map(v => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
                        </TextField>

                        <TextField
                            autoFocus
                            id="number"
                            name="number"
                            label={t('voyage_no')}
                            fullWidth
                            required={!create}
                            inputProps={{ pattern: '[A-Z]{3,4}[0-9]{0,1}[0-9]{4}(?:[A-Z]{3})?' }}
                            value={state.number || ''}
                            onChange={e => setState(old => ({...old, number: e.target.value}))}
                            disabled={create}
                        />

                        <TextField
                            select
                            id="customsYardId"
                            name="customsYardId"
                            label={t('customs_yard')}
                            fullWidth
                            value={state.customsYardId || ''}
                            onChange={e => setState(old => ({...old, customsYardId: Number(e.target.value)}))}
                        >
                            <MenuItem value="">&nbsp;</MenuItem>
                            {yards.map(y => <MenuItem key={y.id} value={y.id}>{y.port.name} {y.name}</MenuItem>)}
                        </TextField>

                        <TextField
                            select
                            id="pol"
                            name="polId"
                            label={t('loading_port')}
                            fullWidth
                            required
                            value={state.polId || ''}
                            onChange={e => setState(old => ({...old, polId: Number(e.target.value)}))}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">
                                    <IconButton
                                        aria-label="add loading port"
                                        onClick={() => openAddPortModal(true).then(polId => setState(old => ({...old, polId})))}
                                    >
                                        <Add />
                                    </IconButton>
                                </InputAdornment>,
                            }}
                        >
                            {loadingPorts.map(p => <MenuItem key={p.id} value={p.id}>{p.name}, {p.country}</MenuItem>)}
                        </TextField>

                        <TextField
                            select
                            id="pod"
                            name="podId"
                            label={t('destination_port')}
                            fullWidth
                            required
                            value={state.podId || ''}
                            onChange={e => setState(old => ({...old, podId: Number(e.target.value)}))}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">
                                    <IconButton
                                        aria-label="add destination port"
                                        onClick={() => openAddPortModal(false).then(podId => setState(old => ({...old, podId})))}
                                    >
                                        <Add />
                                    </IconButton>
                                </InputAdornment>,
                            }}
                        >
                            {destinationPorts.map(p => <MenuItem key={p.id} value={p.id}>{p.name}, {p.country}</MenuItem>)}
                        </TextField>

                        <TextField
                            id="freightPrice"
                            name="freightPrice"
                            label="Base freight, $"
                            fullWidth
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            value={state.freightPrice || ''}
                            onChange={e => setState(old => ({...old, freightPrice: Number(e.target.value)}))}
                        />

                        <FormControlLabel
                            name="allowDismantled"
                            value="1"
                            label={t('allow_dismantled_cars')}
                            control={<Checkbox
                                defaultChecked={Boolean(state.allowDismantled)}
                                onChange={e => setState(old => ({...old, allowDismantled: e.target.checked}))}
                            />}
                        />
                    </Grid>
                    <Grid item xs={12} sm={7}>
                        <Grid container columnSpacing={2}>
                            {isTransfer ? (
                                <Box height={57} />
                            ) : (
                                <Grid item xs={6}>
                                    <DatePicker
                                        label={t('docs_deadline')}
                                        format="yyyy-MM-dd"
                                        value={dates.cutDate}
                                        onChange={setCutDate}
                                        slotProps={{ textField: { fullWidth: true, required: true } }}
                                    />
                                </Grid>
                            )}

                            <Box width="100%"/>

                            {isTransfer ? (
                                <Box height={57} width="100%" />
                            ) : (
                                <>
                                    <Grid item xs={6}>
                                        <DatePicker
                                            label={t('car_delivery_start_date')}
                                            format="yyyy-MM-dd"
                                            value={dates.carDeliveryStartDate}
                                            onChange={d => setDates(old => ({...old, carDeliveryStartDate: d}))}
                                            slotProps={{ textField: { fullWidth: true, required: true } }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <DatePicker
                                            label={t('car_delivery_end_date')}
                                            format="yyyy-MM-dd"
                                            minDate={dates.carDeliveryStartDate}
                                            value={dates.carDeliveryEndDate}
                                            onChange={d => setDates(old => ({...old, carDeliveryEndDate: d}))}
                                            slotProps={{ textField: { fullWidth: true, required: true } }}
                                        />
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={6}>
                                <DatePicker
                                    label={t('loading_start_date')}
                                    format="yyyy-MM-dd"
                                    minDate={dates.carDeliveryEndDate!}
                                    value={dates.loadingStartDate}
                                    onChange={setLoadingStart}
                                    slotProps={{ textField: { fullWidth: true, required: true } }}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <DatePicker
                                    label={t('loading_end_date')}
                                    format="yyyy-MM-dd"
                                    minDate={dates.loadingStartDate}
                                    value={dates.loadingEndDate}
                                    onChange={d => setDates(old => ({...old, loadingEndDate: d}))}
                                    slotProps={{ textField: { fullWidth: true, required: true } }}
                                />
                            </Grid>

                            <Box width="100%"/>

                            <Grid item xs={6}>
                                <DatePicker
                                    label={t('etd')}
                                    format="yyyy-MM-dd"
                                    minDate={dates.loadingEndDate}
                                    value={dates.etd}
                                    onChange={d => setDates(old => ({...old, etd: d}))}
                                    slotProps={{ textField: { fullWidth: true, required: true } }}
                                />
                            </Grid>

                            <Box width="100%"/>

                            <Grid item xs={6}>
                                <DatePicker
                                    label={t('eta')}
                                    format="yyyy-MM-dd"
                                    minDate={dates.etd!}
                                    value={dates.eta}
                                    onChange={setEta}
                                    slotProps={{ textField: { fullWidth: true, required: true } }}
                                />
                            </Grid>

                            <Box width="100%"/>

                            <Grid item xs={6}>
                                <DatePicker
                                    label={t('ru_bl_issue_date')}
                                    format="yyyy-MM-dd"
                                    minDate={dates.eta}
                                    value={dates.blIssueDate}
                                    onChange={d => setDates(old => ({...old, blIssueDate: d}))}
                                    slotProps={{ textField: { fullWidth: true, required: true } }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button type="submit">{create ? 'Add' : 'Save'}</Button>
            </DialogActions>
        </Box>
    );
};

export default VoyageDialog;
